import { InventoryTagCreatedEvent } from '@rematter/frontend-lib-api-client/grpc/inventory/v1/inventory_tag_events_pb';
import {
  InventoryTagStatus,
  TagType,
  QuantityCaptureMethod,
} from '@rematter/frontend-lib-api-client/grpc/inventory/v1/inventory_tag_pb';
import {
  Col,
  humanReadableDateTime,
  FieldDisplay,
  IDField,
} from '@rematter/ui-web';

import {
  inventoryTagStatusLabels,
  tagTypeLabels,
  quantityCaptureMethodLabels,
} from '../enumLabels';

import { protoTimestampToZonedDateTimeString } from '@/audit-log/_components/_shared';
import { getLocale } from '~/shared/utils/locale';

type InventoryTagCreatedDetailProps = {
  event: InventoryTagCreatedEvent;
};

const getStatusLabel = (status: InventoryTagStatus) =>
  inventoryTagStatusLabels[status] ?? 'Unknown';

const getTypeLabel = (tagType: TagType) => tagTypeLabels[tagType] ?? 'Unknown';

const getQuantityCaptureMethodLabel = (method: QuantityCaptureMethod) =>
  quantityCaptureMethodLabels[method] ?? 'Unknown';

export const InventoryTagCreatedDetail = ({
  event,
}: InventoryTagCreatedDetailProps) => {
  const tag = event.inventoryTag;

  if (!tag) {
    return (
      <Col className="@InventoryTagCreatedDetail items-center justify-center p-4 text-neutral/40">
        <span>No data available</span>
      </Col>
    );
  }

  return (
    <Col className="@InventoryTagCreatedDetail gap-3" gap3>
      <Col className="rounded-md border border-neutral/10 bg-white p-3" gap2>
        <span className="text-xs font-medium text-neutral/60">
          Inventory Tag Details
        </span>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <IDField label="ID" value={tag.id?.value} />
          <FieldDisplay label="Tag Number" value={tag.tagNumber} />
          <FieldDisplay label="Tag Type" value={getTypeLabel(tag.tagType)} />
          <FieldDisplay label="Status" value={getStatusLabel(tag.status)} />
          <FieldDisplay
            label="Gross Quantity"
            value={String(tag.grossQuantity)}
          />
          <FieldDisplay
            label="Tare"
            value={tag.tare != null ? String(tag.tare) : 'N/A'}
          />
          <IDField label="Item ID" value={tag.itemId?.value} />
          <IDField label="Facility ID" value={tag.facilityId?.value} />
          <FieldDisplay
            label="Is Revised"
            value={tag.isRevised ? 'Yes' : 'No'}
          />
          <FieldDisplay
            label="Quantity Capture Method"
            value={getQuantityCaptureMethodLabel(tag.quantityCaptureMethod)}
          />
          <FieldDisplay
            label="Tare Capture Method"
            value={getQuantityCaptureMethodLabel(tag.tareCaptureMethod)}
          />
          {tag.generatedAt && (
            <FieldDisplay
              label="Generated At"
              value={humanReadableDateTime(
                protoTimestampToZonedDateTimeString(tag.generatedAt) || '',
                { locale: getLocale(), includeYear: true },
              )}
            />
          )}
        </div>
      </Col>
    </Col>
  );
};
